import React, { useState, useEffect, useMemo, useRef } from 'react';

import { Modal } from 'flowbite';

import GeneralRiskImpactCategoriesTable from './GeneralRiskImpactCategoriesTable';
import GeneralRiskImpactCategoryCreateModal from './GeneralRiskImpactCategoryCreateModal';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import Toast from 'CommonComponents/Toast';

const CREATE_MODAL_ID = 'create-general-risk-impact-category-modal';
const DELETE_MODAL_ID = 'delete-general-risk-impact-category-modal';

export default function GeneralRiskImpactCategoriesTab(props) {
    const [impactCategories, setImpactCategories] = useState([]);
    const [deletableImpactCategory, setDeletableImpactCategory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [generalRiskImpactCategoryDeleteModalElement, setGeneralRiskImpactCategoryDeleteModalElement] = useState(null);
    const [generalRiskImpactCategoryCreateModalElement, setGeneralRiskImpactCategoryCreateModalElement] = useState(null);

    const generalRiskImpactCategoryDeleteModalRef = useRef();
    const generalRiskImpactCategoryCreateModalRef = useRef();

    const deleteUrl = useMemo(
        () => deletableImpactCategory && props.deleteUrl.replace('IMPACT_CATEGORY_ID', deletableImpactCategory.id),
        [deletableImpactCategory],
    );

    useEffect(() => {
        fetchGeneralRiskImpactCategories();
        generalRiskImpactCategoryDeleteModalRef &&
            setGeneralRiskImpactCategoryDeleteModalElement(new Modal(generalRiskImpactCategoryDeleteModalRef.current, modalOptions(false)));
        generalRiskImpactCategoryCreateModalRef &&
            setGeneralRiskImpactCategoryCreateModalElement(new Modal(generalRiskImpactCategoryCreateModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        deletableImpactCategory && showModal(generalRiskImpactCategoryDeleteModalElement);
    }, [deletableImpactCategory]);

    const showModal = (modalElement) => {
        modalElement && modalElement.show();
    };

    // After a create, edit or delete: reload the list and tell an embedding
    // page that the data its status depends on has changed.
    const refetchAfterChange = () => {
        fetchGeneralRiskImpactCategories();

        if (props.onChanged) {
            props.onChanged();
        }
    };

    const fetchGeneralRiskImpactCategories = () => {
        setIsLoading(true);
        axios
            .get(props.fetchUrl)
            .then(({ data }) => {
                setImpactCategories([...data]);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeleteImpactCategory = () => {
        setActionInProgress(true);

        axios
            .delete(deleteUrl)
            .then((res) => {
                generalRiskImpactCategoryDeleteModalElement && generalRiskImpactCategoryDeleteModalElement.hide();
                refetchAfterChange();
                $('#general-risk-impact-category-deleted-toast').toast('show');
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setActionInProgress(false);
            });
    };

    return (
        <div>
            <div className={'mb-2 d-flex justify-content-end'}>
                <Button variant="primary" onClick={() => showModal(generalRiskImpactCategoryCreateModalElement)}>
                    {$t('Create Risk Impact Category')}
                </Button>
            </div>

            <GeneralRiskImpactCategoriesTable
                isLoading={isLoading}
                impactCategories={impactCategories}
                updateUrl={props.updateUrl}
                fetch={refetchAfterChange}
                onDelete={setDeletableImpactCategory}
            />

            <GeneralRiskImpactCategoryCreateModal
                modalId={CREATE_MODAL_ID}
                modalRef={generalRiskImpactCategoryCreateModalRef}
                modalElement={generalRiskImpactCategoryCreateModalElement}
                onCreated={refetchAfterChange}
                storeUrl={props.storeUrl}
            />

            <ConfirmationModal
                successHandler={refetchAfterChange}
                triggerAction={triggerDeleteImpactCategory}
                cleanStates={cleanConfirmationModalStates}
                allowAction={deletableImpactCategory}
                modalId={DELETE_MODAL_ID}
                modalRef={generalRiskImpactCategoryDeleteModalRef}
                modalElement={generalRiskImpactCategoryDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete this {{title}}?', { title: deletableImpactCategory?.title })}
                actionCompletedMsg=""
                actionFailedMsg=""
                title={$t('Delete {{title}}', { title: deletableImpactCategory?.title })}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('OK')}
            />

            <Toast id="general-risk-impact-category-update-toast" message={$t('general risk impact category updated successfully')} />
            <Toast id="general-risk-impact-category-update-failed-toast" message={$t('general risk impact category update failed')} />
            <Toast id="general-risk-impact-category-deleted-toast" message={$t('general risk impact category deleted successfully')} />
        </div>
    );
}
